// Estimated burn — the arithmetic behind the day view's deficit bar.
//
// Pure functions only: no React, no network, no SharePoint. Every row this
// module reads is handed to it as plain data by Food.js, which is what makes it
// loadable by the probe shim the same way foodEntry.js is. Nothing here writes
// anything anywhere — the bar is display-only and stores no computed value on
// any row.
//
// DATES. This module deliberately does NOT use fromDateOnly. That helper is a
// string slice, and figApi's guard block (figApi.js:975-1004) explains why it is
// sound only for Food_Log.Date, whose every row is written in the bare
// 'YYYY-MM-DD' shape. SwimmingWorkouts.WorkoutDate and FitnessWeighIns.WeighDate
// are neither guarded nor bare: both are written as new Date().toISOString()
// (SwimmingWorkout.js:637, SwimmingHistory.js:97). WalkingWorkouts.WorkoutDate
// is likewise a full ISO instant — picker-built and converted through the local
// Date constructor (WalkingLog.js:135) — and reads through the same conversion.
// Slicing those would file a 7pm-Central workout under tomorrow —
// 2026-08-03T19:00 Central is 2026-08-04T00:00Z and the slice reads
// '2026-08-04'. localDateKey below converts through local components instead,
// mirroring dateKey at SwimmingDashboard.js:91.
//
// ABSENCE. Same rule as the rest of the module: absent is not zero. A missing
// weight yields null and the caller renders a dash — there is no default weight
// and no `|| 0` anywhere in this file. SwimmingHistory.js:41-56 maps these very
// columns with `WeightLbs || 0`, which collapses a missing weigh-in and a
// zero-pound one into the same reading. That pattern is not copied here.

// The walking model lives with the other fitness models and already nets out
// resting through restingCalPerDay below — importing it rather than restating
// it keeps ONE walking model in the codebase. This import closes a cycle
// (walkingBurn.js imports restingCalPerDay from this file); that is safe
// because each side touches the other's exports only at call time, never
// during module evaluation.
import { walkBurnEstimate } from '../fitness/walkingBurn';

// The two figures no list records. There is no height, birthdate or age column
// on FitnessWeighIns or anywhere else in the tenant, so per Spec v4 §7.1 they
// are hardcoded at the point of use rather than invented into a schema.
const HEIGHT_CM = 177.8;  // 5'10"

// As of 2026-08. This WILL go stale on a birthday and nothing in the code or the
// data can catch it — there is no birthdate to derive it from. A year of drift
// moves resting burn by 5 kcal/day, which is inside the rounding this bar
// already applies, but it compounds.
const AGE_YEARS = 54;

// Mifflin-St Jeor's sex term: +5 male, -161 female. Named rather than inlined so
// the formula below cannot be read as if it applied to anyone.
const MIFFLIN_MALE_CONSTANT = 5;

// Desk job plus the thermic effect of food. Applied to resting, not to swim —
// the swim is measured separately and multiplying it here would count it twice.
// The point value the bar's geometry uses; LOW/HIGH bracket it for display,
// because a figure built on a hardcoded height and an activity guess deserves
// to be shown as the range it is, not the point it is not.
const SEDENTARY_MULTIPLIER = 1.22;
const SEDENTARY_MULTIPLIER_LOW = 1.15;
const SEDENTARY_MULTIPLIER_HIGH = 1.30;

// Metabolic equivalent for swimming, moderate effort. LOW/HIGH span the
// published spread from easy freestyle to vigorous effort; same role as the
// multiplier bracket above — display range, not bar math.
const SWIM_MET = 7;
const SWIM_MET_LOW = 5.8;
const SWIM_MET_HIGH = 9.8;

const LBS_PER_KG = 2.2046;

// Minutes in a day, for converting a daily resting figure to a per-minute one.
const MINUTES_PER_DAY = 1440;

// The track is a runway, and burn is its ONE meaningful boundary. The scale
// runs 0 to burn × 1.4, which puts burn at ~71% and keeps the surplus region
// visible without inventing any interior boundary to do it. The old band edges
// (700 / 900 / 1100 as deficits) were never derived from anything — they named
// zones on a scale that has exactly one real line, and at 939 eaten against a
// 2,500 burn they rendered a genuine deficit as failure.
const RUNWAY_SCALE = 1.4;

// A UTC ISO instant -> the LOCAL calendar day it fell on, as 'YYYY-MM-DD'.
// Returns null for an absent or unparseable value rather than a plausible-
// looking wrong day; the caller drops the row instead of misfiling it.
export function localDateKey(isoInstant) {
  if (isoInstant === '' || isoInstant === null || isoInstant === undefined) return null;
  const d = new Date(isoInstant);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Mifflin-St Jeor, male. kcal/day at complete rest.
//
// Throws rather than coerces on a non-finite weight, for the same reason
// toDateOnly throws: a resting figure computed from NaN would propagate silently
// into a burn number that looks authoritative. Callers reach this only after
// weightLbsForDay has returned an actual number.
export function restingCalPerDay(kg) {
  if (typeof kg !== 'number' || !Number.isFinite(kg)) {
    throw new Error(`restingCalPerDay: expected a finite weight in kg, got ${JSON.stringify(kg)}`);
  }
  return (10 * kg) + (6.25 * HEIGHT_CM) - (5 * AGE_YEARS) + MIFFLIN_MALE_CONSTANT;
}

// Moving seconds from one workout's Sets column, plus whether the capture can be
// trusted as complete.
//
// DurationSeconds is not consulted anywhere in this module. It is ELAPSED time —
// it includes every second spent standing at the wall — and SwimmingDashboard
// uses it only as the denominator of a moving-percentage for exactly that
// reason. Burn is paid for by moving time, so moving time is what gets summed.
//
// `incomplete` marks a sum that is a floor rather than a total. Three cases, and
// they are not the same thing:
//
//   repTimes absent            -> the swim happened, the timing did not. The
//                                 distance is on the row and contributes zero
//                                 seconds here. Flagged: the day is understated.
//   repTimes.length < reps     -> capture stopped part way. Sum what is there,
//                                 never extrapolate the missing reps. Flagged.
//   partial: true              -> NOT flagged. The set was cut short and
//                                 actualReps was rewritten DOWN to the number
//                                 actually swum (SwimmingWorkout.js:601-605), so
//                                 the capture is complete for what happened.
//
// A Sets value that will not parse returns zero seconds and flags — malformed
// JSON is the strongest possible evidence that the capture is not complete, and
// throwing here would take the day view down over one bad row.
export function movingSecondsFromSets(setsJsonString) {
  let sets;
  try {
    sets = JSON.parse(setsJsonString);
  } catch {
    return { seconds: 0, incomplete: true };
  }
  if (!Array.isArray(sets)) return { seconds: 0, incomplete: true };
  // A workout row carrying no sets at all recorded no moving time for a session
  // that demonstrably happened — the same floor the absent-repTimes case
  // produces, so it flags for the same reason.
  if (sets.length === 0) return { seconds: 0, incomplete: true };

  let seconds = 0;
  let incomplete = false;

  for (const set of sets) {
    if (set === null || typeof set !== 'object') {
      incomplete = true;
      continue;
    }

    const repTimes = Array.isArray(set.repTimes) ? set.repTimes : null;
    if (repTimes === null) {
      incomplete = true;
      continue;
    }

    // Same filter the existing swim paths apply: a non-positive or non-numeric
    // split is not a swim, and Number('') would otherwise sum as 0 unnoticed.
    const timed = repTimes.map(Number).filter(t => Number.isFinite(t) && t > 0);
    for (const t of timed) seconds += t;

    // actualReps is what the writer stamped for this set; reps is the planned
    // figure it falls back to. When neither is a usable number there is no
    // expectation to compare against, so length cannot be judged short.
    const reps = Number(set.actualReps ?? set.reps);
    if (Number.isFinite(reps) && timed.length < reps) incomplete = true;
  }

  return { seconds, incomplete };
}

// Swim kcal across a day's workouts, net of what he would have burned sitting
// still for the same minutes.
//
// Gross MET kcal counts the whole cost of being alive while swimming, and the
// sedentary figure this is added to has already paid for those minutes once.
// Subtracting the resting share is what keeps the two from double-counting.
//
// Floored per workout rather than on the total: a session so short that the
// resting subtraction exceeds its gross should contribute nothing, not a credit
// against the session next to it.
// `met` defaults to the point estimate; computeBurn also calls this at the
// bracket METs for the display range. One summation (movingSecondsFromSets),
// three readings of it.
export function swimCalFromWorkouts(workouts, kg, met = SWIM_MET) {
  const restingPerMin = restingCalPerDay(kg) / MINUTES_PER_DAY;

  let cal = 0;
  let incomplete = false;

  for (const w of workouts) {
    const moving = movingSecondsFromSets(w.Sets);
    if (moving.incomplete) incomplete = true;

    const movingMin = moving.seconds / 60;
    const gross = movingMin * met * 3.5 * kg / 200;
    const net = gross - (movingMin * restingPerMin);
    cal += Math.max(0, net);
  }

  return { cal, incomplete };
}

// The weigh-in row in force on a given day: the most recent one dated on or
// before it. Not the newest row overall — computing a June burn from an August
// weight would be wrong in the direction of whatever progress was made since.
//
// Rows whose WeightLbs is absent or non-numeric are skipped rather than read as
// zero. Ties within a single day fall to the later instant.
function weighInForDay(weighIns, dayKey) {
  let best = null;
  let bestKey = null;
  let bestTime = null;

  for (const row of weighIns) {
    const lbs = row.WeightLbs;
    if (typeof lbs !== 'number' || !Number.isFinite(lbs)) continue;

    const key = localDateKey(row.WeighDate);
    if (key === null || key > dayKey) continue;

    const time = new Date(row.WeighDate).getTime();
    if (bestKey === null || key > bestKey || (key === bestKey && time > bestTime)) {
      best = row;
      bestKey = key;
      bestTime = time;
    }
  }

  return best === null ? null : { weightLbs: best.WeightLbs, dayKey: bestKey };
}

// Pounds in force on `dayKey`, or null when no weigh-in precedes it. Null is a
// real answer here and the caller renders the whole bar as a dash for it: an
// absent weight is not a default weight, and a burn computed from a guess would
// be indistinguishable on screen from one computed from a measurement.
export function weightLbsForDay(weighIns, dayKey) {
  const found = weighInForDay(weighIns, dayKey);
  return found === null ? null : found.weightLbs;
}

// The day's whole burn picture, or null when there is no weight to build it on.
//
// `workouts` (swims) and `walks` are the unfiltered lists; the day match
// happens here so that the local-date conversion lives in one place.
// `weighIns` likewise. `walks` defaults to empty so a caller that predates
// the walking term still computes — absence of the argument is absence of
// walks, not an error.
export function computeBurn({ weighIns, workouts, walks = [], dayKey }) {
  const weighIn = weighInForDay(weighIns, dayKey);
  if (weighIn === null) return null;

  const kg = weighIn.weightLbs / LBS_PER_KG;
  const resting = restingCalPerDay(kg);

  const dayWorkouts = workouts.filter(w => localDateKey(w.WorkoutDate) === dayKey);
  const swim = swimCalFromWorkouts(dayWorkouts, kg);
  const swimLow = swimCalFromWorkouts(dayWorkouts, kg, SWIM_MET_LOW);
  const swimHigh = swimCalFromWorkouts(dayWorkouts, kg, SWIM_MET_HIGH);

  // The day's swim volume, for the subline under the burn range. Summed from
  // the rows' own TotalDistanceMeters; 0 when no workout landed on this day.
  let swimMeters = 0;
  for (const w of dayWorkouts) {
    const m = Number(w.TotalDistanceMeters);
    if (Number.isFinite(m) && m > 0) swimMeters += m;
  }

  // The walk term. walkBurnEstimate is the tested model from the fitness
  // side and is reused as-is, which sets three shapes deliberately:
  //
  //   per-walk    it estimates one walk, so the day's low/mid/high are summed
  //               across rows bound-by-bound — the same three-readings idea
  //               as calling swimCalFromWorkouts at the three METs.
  //   rounded     its outputs arrive pre-rounded to 10. That ±5-per-walk sits
  //               inside the 50-step every displayed figure rounds to, and
  //               unrounding it would mean modifying the tested file.
  //   null        a row it cannot estimate (unusable effort, or neither
  //               metric usable) contributes zero and FLAGS the day — the
  //               walk happened, so the total is a floor, exactly what
  //               `incomplete` already means for an untimed swim. Its
  //               `suspect` flag is ignored here: that is the entry form's
  //               data-entry warning, and the estimate is still the best
  //               available.
  //
  // The per-session floor-at-zero needs nothing here — netWalkCal floors
  // inside the model, the same convention as the swim term's Math.max(0).
  const dayWalks = walks.filter(w => localDateKey(w.WorkoutDate) === dayKey);
  let walkCal = 0;
  let walkLowCal = 0;
  let walkHighCal = 0;
  let walkIncomplete = false;
  // The subline's figures, independent of estimability — the subline reports
  // what was logged, not what was estimable, the same posture as swimMeters
  // counting an untimed swim's distance. Minutes accrue only from rows with
  // no distance: when any row has miles, miles name the day.
  let walkMiles = 0;
  let walkMinutes = 0;
  for (const w of dayWalks) {
    const est = walkBurnEstimate({
      distanceMiles: w.DistanceMiles,
      durationSeconds: w.DurationSeconds,
      effort: w.Effort,
      weightLbs: weighIn.weightLbs,
    });
    if (est === null) {
      walkIncomplete = true;
    } else {
      walkLowCal += est.low;
      walkCal += est.mid;
      walkHighCal += est.high;
    }

    const mi = Number(w.DistanceMiles);
    if (Number.isFinite(mi) && mi > 0) {
      walkMiles += mi;
    } else {
      const sec = Number(w.DurationSeconds);
      if (Number.isFinite(sec) && sec > 0) walkMinutes += sec / 60;
    }
  }

  return {
    // The point value every piece of bar geometry uses.
    burnCal: resting * SEDENTARY_MULTIPLIER + swim.cal + walkCal,
    // Display-only bracket. The bar never positions anything with these.
    burnLowCal: resting * SEDENTARY_MULTIPLIER_LOW + swimLow.cal + walkLowCal,
    burnHighCal: resting * SEDENTARY_MULTIPLIER_HIGH + swimHigh.cal + walkHighCal,
    weightLbs: weighIn.weightLbs,
    weighInDayKey: weighIn.dayKey,
    swimCal: swim.cal,
    swimMeters,
    walkCal,
    walkMiles,
    walkMinutes,
    incomplete: swim.incomplete || walkIncomplete,
  };
}

// The runway's geometry, from the two numbers that mean anything: intake and
// burn. The scale runs 0 .. burn × RUNWAY_SCALE, so burn sits at the same
// fraction every day (~71%) and the region past it stays visible.
//
// Three percents, four segments for the caller to paint, no names and no
// verdicts:
//
//   0        .. eatenPct   solid — what has been eaten (stops at burn)
//   eatenPct .. burnPct    pale  — the remaining runway (zero-width when over)
//   burnPct  .. overPct    solid — the surplus actually eaten (zero-width when under)
//   the rest ..            pale  — the surplus region not reached
//
// Clamped at both ends: an intake past the right edge pins there. Everything
// left of burnPct is deficit, everything right of it is surplus, and nothing
// on the track ranks one position over another.
export function runwayPositions(intakeCal, burnCal) {
  if (typeof intakeCal !== 'number' || !Number.isFinite(intakeCal)) {
    throw new Error(`runwayPositions: expected a finite intake, got ${JSON.stringify(intakeCal)}`);
  }
  if (typeof burnCal !== 'number' || !Number.isFinite(burnCal) || burnCal <= 0) {
    throw new Error(`runwayPositions: expected a positive burn, got ${JSON.stringify(burnCal)}`);
  }

  const max = burnCal * RUNWAY_SCALE;
  const pct = cal => Math.min(100, Math.max(0, (cal / max) * 100));

  return {
    burnPct: pct(burnCal),
    eatenPct: pct(Math.min(intakeCal, burnCal)),
    overPct: pct(intakeCal),
  };
}
